import React from "react";
import {
  Box,
  CircularProgress,
  Paper,
  Typography,
  Grid,
  Chip,
  Card,
  CardContent,
  IconButton,
  Button,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import BugReportIcon from "@mui/icons-material/BugReport";
import TabPanel from "../TabPanel.js";
import DivesitesGrid from "./DivesitesGrid";
import DivesitesTable from "./DivesitesTable";
import WeatherCard from "./WeatherCard.js";
import TidesCard from "./TidesCard.js";
import LocationCard from "./LocationCard.js";
import ActivityCard from "./ActivityCard.js";
import CreaturesDashboard from "./CreaturesDashboard"; // Import the creatures dashboard
import RefreshIcon from "@mui/icons-material/Refresh";

const DivesiteContent = ({
  tabValue,
  loading,
  error,
  refresh,
  divesites, // This will be the dashboardDivesites array
  paginatedDivesites,
  viewMode,
  handleDivesiteClick,
  onRemoveDivesite,
  isDeleting,
  availableSlots,
  onAddDivesite,
}) => {
  const renderDivesites = () => {
    if (loading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress />
        </Box>
      );
    }

    if (error) {
      return <Typography color="error">Error loading divesites.</Typography>;
    }

    if (!divesites || divesites.length === 0) {
      return availableSlots > 0 ? (
        <Paper
          sx={{
            p: 3,
            textAlign: "center",
            borderRadius: 2,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Your dashboard is empty.
          </Typography>
          {availableSlots > 0 && (
            <Card
              onClick={onAddDivesite}
              sx={{
                width: 150,
                height: 150,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "1px dashed grey",
                cursor: "pointer",
              }}
            >
              <CardContent sx={{ textAlign: "center" }}>
                <IconButton color="primary">
                  <AddIcon fontSize="large" />
                </IconButton>
                <Typography variant="body2" color="textSecondary">
                  Add Divesite
                </Typography>
              </CardContent>
            </Card>
          )}
        </Paper>
      ) : (
        <Paper sx={{ p: 3, textAlign: "center", borderRadius: 2 }}>
          <Typography variant="h6" gutterBottom>
            Your dashboard is empty.
          </Typography>
          <Typography color="textSecondary">
            You have no available slots to add divesites.
          </Typography>
        </Paper>
      );
    }

    const divesiteList =
      paginatedDivesites.length > 0 ? paginatedDivesites : divesites;

    if (viewMode === "grid") {
      return (
        <>
          <DivesitesGrid
            divesites={divesiteList}
            onDivesiteClick={handleDivesiteClick}
            onRemoveDivesite={onRemoveDivesite}
            isDeleting={isDeleting}
          />
          {availableSlots > 0 && (
            <Box sx={{ mt: 2 }}>
              <Chip
                label={`${availableSlots} Slots Available`}
                color="info"
                size="small"
                sx={{ mb: 1 }}
              />
              <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={4} lg={3}>
                  <Card
                    onClick={onAddDivesite}
                    sx={{
                      height: "100%",
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      border: "1px dashed grey",
                      cursor: "pointer",
                    }}
                  >
                    <CardContent sx={{ textAlign: "center" }}>
                      <IconButton color="primary">
                        <AddIcon fontSize="large" />
                      </IconButton>
                      <Typography variant="body2" color="textSecondary">
                        Add Divesite
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Box>
          )}
        </>
      );
    } else if (viewMode === "table") {
      return (
        <>
          <DivesitesTable
            divesites={divesiteList}
            onDivesiteClick={handleDivesiteClick}
            onRemoveDivesite={onRemoveDivesite}
            isDeleting={isDeleting}
          />
          {availableSlots > 0 && (
            <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
              <Chip
                label={`${availableSlots} Slots Available`}
                color="info"
                size="small"
                sx={{ mr: 2 }}
              />
              <Button
                variant="outlined"
                color="primary"
                onClick={onAddDivesite}
              >
                Add Divesite
              </Button>
            </Box>
          )}
        </>
      );
    }
    return null;
  };

  const renderCardGrid = (CardComponent) => (
    <Grid container spacing={3}>
      {Array.isArray(paginatedDivesites) &&
        paginatedDivesites.map((divesite) => (
          <Grid
            item
            key={divesite._id}
            xs={12}
            sm={viewMode === "list" ? 12 : 6}
            md={viewMode === "list" ? 12 : 4}
            lg={viewMode === "list" ? 12 : 4}
          >
            <CardComponent divesite={divesite} />
          </Grid>
        ))}
      {!Array.isArray(paginatedDivesites) && (
        <Typography color="error">
          Error: Divesites data is not an array.
        </Typography>
      )}
      {Array.isArray(paginatedDivesites) && paginatedDivesites.length === 0 && (
        <Typography color="textSecondary">
          No divesites available to show.
        </Typography>
      )}
    </Grid>
  );

  return (
    <>
      <TabPanel value={tabValue} index={0}>
        {renderDivesites()}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {renderCardGrid(WeatherCard)}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {renderCardGrid(TidesCard)}
      </TabPanel>

      <TabPanel value={tabValue} index={3}>
        {renderCardGrid(LocationCard)}
      </TabPanel>

      <TabPanel value={tabValue} index={4}>
        {renderCardGrid(ActivityCard)}
      </TabPanel>

      {/* New Creatures Tab */}
      <TabPanel value={tabValue} index={5}>
        {console.log("Divesites for CreaturesDashboard:", divesites)}
        <CreaturesDashboard dashboardDivesites={divesites} />
      </TabPanel>
    </>
  );
};

export default DivesiteContent;
